/** @jsxImportSource @emotion/react */
import React, { useMemo } from 'react'
import { css } from '@emotion/react'
import { Popover } from 'antd'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import timezone from 'dayjs/plugin/timezone'
import colors from '@sportninja/common/constants/appColors'
import type { CalendarGame } from './CalendarGameCard'
import type { Venue } from '../hooks/useSchedulerLocations'

dayjs.extend(utc)
dayjs.extend(timezone)

// ─── Constants ────────────────────────────────────────────────────
const TIME_COL_WIDTH = 120
const HOUR_HEIGHT = 100
const DEFAULT_GAME_DURATION = 60
const GRID_BORDER_COLOR = '#2f3847'
const VENUE_COLORS = [
  '#4a90e2', // Blue
  '#e2994a', // Orange
  '#9b4ae2', // Purple
  '#4ae2a8', // Green
  '#e24a6e', // Rose
  '#4acde2', // Cyan
  '#e2d44a', // Gold
  '#7ae24a', // Lime
]

const POPOVER_OVERLAY_STYLE = { zIndex: 1050 }
const POPOVER_INNER_STYLE = {
  background: colors.SECONDARY_300,
  border: `1px solid ${colors.SECONDARY_100}`,
  borderRadius: '8px',
  padding: 0,
}

// ─── Types ────────────────────────────────────────────────────────
interface FacilityColumn {
  venueId: string
  venueName: string
  facilityId: string
  facilityName: string
  venueColor: string
}

interface DayGridProps {
  selectedDate: Date
  games: CalendarGame[]
  venues: Venue[]
  onGameClick?: (game: CalendarGame) => void
  popoverGameId?: string | null
  popoverContent?: React.ReactNode
  onPopoverOpenChange?: (gameId: string | null) => void
}

// ─── Helpers ──────────────────────────────────────────────────────
function buildFacilityColorMap(venues: Venue[]): Map<string, string> {
  const map = new Map<string, string>()
  for (let idx = 0; idx < venues.length; idx++) {
    const color = VENUE_COLORS[idx % VENUE_COLORS.length]
    for (const facility of venues[idx].facilities) {
      map.set(facility.id, color)
    }
  }
  return map
}

function computeTimeWindow(dayGames: CalendarGame[]): {
  startHour: number
  endHour: number
} {
  if (dayGames.length === 0) {
    return { startHour: 8, endHour: 21 }
  }
  let minHour = 23
  let maxHour = 0
  for (const game of dayGames) {
    const tz = game.timezone || 'UTC'
    const d = dayjs.utc(game.startsAt || game.date.toISOString()).tz(tz)
    if (!d.isValid()) {
      continue
    }
    const h = d.hour()
    if (h < minHour) {
      minHour = h
    }
    // Compute end hour from endsAt or fallback
    if (game.endsAt) {
      const end = dayjs.utc(game.endsAt).tz(tz)
      if (end.isValid()) {
        const eh = end.hour() + (end.minute() > 0 ? 1 : 0)
        if (eh > maxHour) {
          maxHour = eh
        }
      }
    } else {
      const fallbackEnd = h + 1
      if (fallbackEnd > maxHour) {
        maxHour = fallbackEnd
      }
    }
  }
  return {
    startHour: Math.max(0, minHour - 1),
    endHour: Math.min(24, maxHour + 1),
  }
}

function buildFacilityColumns(
  dayGames: CalendarGame[],
  venues: Venue[],
  colorMap: Map<string, string>
): FacilityColumn[] {
  // Collect unique facility IDs from games on this day
  const facilityIds = new Set<string>()
  for (const game of dayGames) {
    if (game.surfaceId) {
      facilityIds.add(game.surfaceId)
    }
  }

  const columns: FacilityColumn[] = []
  for (const venue of venues) {
    for (const facility of venue.facilities) {
      if (facilityIds.has(facility.id)) {
        columns.push({
          venueId: venue.id,
          venueName: venue.name,
          facilityId: facility.id,
          facilityName: facility.name,
          venueColor: colorMap.get(facility.id) ?? VENUE_COLORS[0],
        })
      }
    }
  }
  return columns
}

function formatHourLabel(hour: number): string {
  if (hour === 0) {
    return '12:00 AM'
  }
  if (hour === 12) {
    return '12:00 PM'
  }
  if (hour < 12) {
    return `${hour}:00 AM`
  }
  return `${hour - 12}:00 PM`
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

// ─── DayGameCard ──────────────────────────────────────────────────
interface DayGameCardProps {
  game: CalendarGame
  topPx: number
  heightPx: number
  venueColor: string
  onClick?: (game: CalendarGame) => void
  popoverOpen: boolean
  popoverContent?: React.ReactNode
  onPopoverOpenChange?: (gameId: string | null) => void
}

const DayGameCard: React.FC<DayGameCardProps> = ({
  game,
  topPx,
  heightPx,
  venueColor,
  onClick,
  popoverOpen,
  popoverContent,
  onPopoverOpenChange,
}) => {
  const accentColor = venueColor

  const handleClick = () => {
    onClick?.(game)
  }

  const cardContent = (
    <div
      css={css`
        position: absolute;
        top: ${topPx}px;
        left: 4px;
        right: 4px;
        height: ${Math.max(heightPx - 2, 24)}px;
        background: ${accentColor}44;
        border-left: 2.4px solid ${accentColor};
        border-radius: 4px;
        cursor: pointer;
        overflow: hidden;
        padding: 4px 6px;
        display: flex;
        flex-direction: column;
        gap: 1px;
        z-index: 1;
        transition: filter 0.15s ease;

        &:hover {
          filter: brightness(1.15);
        }
      `}
      onClick={handleClick}
    >
      {game.gameNumber != null && (
        <span
          css={css`
            font-family: 'Inter', sans-serif;
            font-weight: 600;
            font-size: 12px;
            line-height: 13px;
            color: ${accentColor};
          `}
        >
          Game #{game.gameNumber}
        </span>
      )}
      <span
        css={css`
          font-family: 'Inter', sans-serif;
          font-weight: 600;
          font-size: 12px;
          line-height: 14px;
          color: ${colors.WHITE};
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        `}
      >
        {game.homeTeam}
      </span>
      {heightPx > 40 && (
        <>
          <span
            css={css`
              font-family: 'Inter', sans-serif;
              font-weight: 400;
              font-size: 12px;
              line-height: 12px;
              color: #797b7e;
            `}
          >
            vs
          </span>
          <span
            css={css`
              font-family: 'Inter', sans-serif;
              font-weight: 600;
              font-size: 12px;
              line-height: 14px;
              color: ${colors.WHITE};
              overflow: hidden;
              text-overflow: ellipsis;
              white-space: nowrap;
            `}
          >
            {game.awayTeam}
          </span>
        </>
      )}
    </div>
  )

  if (popoverContent && onPopoverOpenChange) {
    return (
      <Popover
        content={popoverContent}
        trigger='click'
        open={popoverOpen}
        onOpenChange={(visible) => {
          onPopoverOpenChange(visible ? game.id : null)
        }}
        placement='right'
        overlayStyle={POPOVER_OVERLAY_STYLE}
        overlayInnerStyle={POPOVER_INNER_STYLE}
        destroyTooltipOnHide
      >
        {cardContent}
      </Popover>
    )
  }

  return cardContent
}

// ─── DayGrid ──────────────────────────────────────────────────────
const DayGrid: React.FC<DayGridProps> = ({
  selectedDate,
  games,
  venues,
  onGameClick,
  popoverGameId,
  popoverContent,
  onPopoverOpenChange,
}) => {
  const venueColorMap = useMemo(() => buildFacilityColorMap(venues), [venues])

  // Filter games for selected day
  const dayGames = useMemo(() => {
    return games.filter((g) => {
      const gameDate = new Date(g.startsAt || g.date)
      return !isNaN(gameDate.getTime()) && isSameDay(gameDate, selectedDate)
    })
  }, [games, selectedDate])

  const { startHour, endHour } = useMemo(
    () => computeTimeWindow(dayGames),
    [dayGames]
  )

  const facilityColumns = useMemo(
    () => buildFacilityColumns(dayGames, venues, venueColorMap),
    [dayGames, venues, venueColorMap]
  )

  const hours = useMemo(() => {
    const arr: number[] = []
    for (let h = startHour; h < endHour; h++) {
      arr.push(h)
    }
    return arr
  }, [startHour, endHour])

  // Group games by facility
  const gamesByFacility = useMemo(() => {
    const map = new Map<string, CalendarGame[]>()
    for (const col of facilityColumns) {
      map.set(col.facilityId, [])
    }
    for (const game of dayGames) {
      const list = map.get(game.surfaceId)
      if (list) {
        list.push(game)
      }
    }
    return map
  }, [dayGames, facilityColumns])

  // Empty state
  if (dayGames.length === 0 || facilityColumns.length === 0) {
    return (
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 80px 16px;
          font-family: 'Inter', sans-serif;
          font-size: 14px;
          color: ${colors.NEUTRAL_50};
        `}
      >
        No games scheduled for this day.
      </div>
    )
  }

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        background: ${colors.SECONDARY_400};
        overflow-x: auto;
      `}
    >
      {/* ─── Sticky Header ─── */}
      <div
        css={css`
          display: flex;
          position: sticky;
          top: 0;
          z-index: 10;
          min-width: ${TIME_COL_WIDTH + facilityColumns.length * 120}px;
        `}
      >
        {/* TIME cell */}
        <div
          css={css`
            width: ${TIME_COL_WIDTH}px;
            min-width: ${TIME_COL_WIDTH}px;
            background: #1f2224;
            display: flex;
            align-items: center;
            justify-content: center;
            padding: 12px 8px;
            border-bottom: 0.8px solid ${GRID_BORDER_COLOR};
            border-right: 0.8px solid ${GRID_BORDER_COLOR};
          `}
        >
          <span
            css={css`
              font-family: 'Inter', sans-serif;
              font-weight: 600;
              font-size: 12px;
              letter-spacing: 1.1px;
              color: #797b7e;
              text-transform: uppercase;
            `}
          >
            TIME
          </span>
        </div>

        {/* Facility header cells */}
        {facilityColumns.map((col) => (
          <div
            key={col.facilityId}
            css={css`
              flex: 1;
              min-width: 120px;
              background: ${col.venueColor}12;
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              padding: 10px 8px;
              gap: 2px;
              border-bottom: 0.8px solid ${GRID_BORDER_COLOR};
              border-right: 0.8px solid ${GRID_BORDER_COLOR};
            `}
          >
            <span
              css={css`
                font-family: 'Inter', sans-serif;
                font-weight: 600;
                font-size: 12px;
                line-height: 13px;
                color: ${col.venueColor};
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                max-width: 100%;
                text-align: center;
              `}
            >
              {col.venueName}
            </span>
            <span
              css={css`
                font-family: 'Inter', sans-serif;
                font-weight: 400;
                font-size: 12px;
                line-height: 12px;
                color: #797b7e;
                overflow: hidden;
                text-overflow: ellipsis;
                white-space: nowrap;
                max-width: 100%;
                text-align: center;
              `}
            >
              {col.facilityName}
            </span>
          </div>
        ))}
      </div>

      {/* ─── Grid Body ─── */}
      <div
        css={css`
          display: flex;
          position: relative;
          min-width: ${TIME_COL_WIDTH + facilityColumns.length * 120}px;
        `}
      >
        {/* Hour labels column */}
        <div
          css={css`
            width: ${TIME_COL_WIDTH}px;
            min-width: ${TIME_COL_WIDTH}px;
          `}
        >
          {hours.map((hour) => (
            <div
              key={hour}
              css={css`
                height: ${HOUR_HEIGHT}px;
                display: flex;
                align-items: flex-start;
                justify-content: flex-end;
                padding: 4px 12px 0 0;
                border-right: 0.8px solid ${GRID_BORDER_COLOR};
                border-bottom: 0.8px solid ${GRID_BORDER_COLOR};
              `}
            >
              <span
                css={css`
                  font-family: 'Inter', sans-serif;
                  font-weight: 500;
                  font-size: 12px;
                  line-height: 14px;
                  color: #797b7e;
                `}
              >
                {formatHourLabel(hour)}
              </span>
            </div>
          ))}
        </div>

        {/* Facility columns with games */}
        {facilityColumns.map((col) => {
          const colGames = gamesByFacility.get(col.facilityId) ?? []
          return (
            <div
              key={col.facilityId}
              css={css`
                flex: 1;
                min-width: 120px;
                position: relative;
              `}
            >
              {/* Hour grid lines + background cells */}
              {hours.map((hour) => (
                <div
                  key={hour}
                  css={css`
                    height: ${HOUR_HEIGHT}px;
                    border-right: 0.8px solid ${GRID_BORDER_COLOR};
                    border-bottom: 0.8px solid ${GRID_BORDER_COLOR};
                    background: ${col.venueColor}0D;
                  `}
                />
              ))}

              {/* Game cards absolutely positioned */}
              {colGames.map((game) => {
                const tz = game.timezone || 'UTC'
                const gameStart = dayjs
                  .utc(game.startsAt || game.date.toISOString())
                  .tz(tz)
                if (!gameStart.isValid()) {
                  return null
                }

                const startMinutesFromGridTop =
                  (gameStart.hour() - startHour) * 60 + gameStart.minute()
                const topPx = (startMinutesFromGridTop / 60) * HOUR_HEIGHT

                let durationMinutes = DEFAULT_GAME_DURATION
                if (game.endsAt) {
                  const gameEnd = dayjs.utc(game.endsAt).tz(tz)
                  if (gameEnd.isValid()) {
                    durationMinutes = Math.max(
                      15,
                      gameEnd.diff(gameStart, 'minute')
                    )
                  }
                }
                const heightPx = (durationMinutes / 60) * HOUR_HEIGHT

                return (
                  <DayGameCard
                    key={game.id}
                    game={game}
                    topPx={topPx}
                    heightPx={heightPx}
                    venueColor={col.venueColor}
                    onClick={onGameClick}
                    popoverOpen={popoverGameId === game.id}
                    popoverContent={
                      popoverGameId === game.id ? popoverContent : undefined
                    }
                    onPopoverOpenChange={onPopoverOpenChange}
                  />
                )
              })}
            </div>
          )
        })}
      </div>
    </div>
  )
}

export default DayGrid
